/**
 * Fibonacci overlay renderer — anchor swing line, retracement/extension
 * horizontal levels, and the 0.618-0.786 Golden Zone shaded band.
 */
import type { FibonacciOverlay } from '@trading/shared';
import type { OverlayDrawContext } from './types';

const KEY_RATIOS = new Set([0.382, 0.5, 0.618, 0.786]);

export function drawFibonacciOverlay(data: FibonacciOverlay, c: OverlayDrawContext): void {
  const { ctx, height, defaultZoneStartX, zoneEndX, getYFromPrice, getXFromTime } = c;

  // Determine the anchor X span. Levels render from the anchor end to the
  // right edge so the trader sees the projected reaction zone.
  let anchorEndX = defaultZoneStartX;
  if (data.anchor) {
    anchorEndX = getXFromTime(data.anchor.endTime, defaultZoneStartX);

    // Anchor swing line from start to end.
    const anchorStartX = getXFromTime(data.anchor.startTime, defaultZoneStartX);
    const startY = getYFromPrice(data.anchor.startPrice);
    const endY = getYFromPrice(data.anchor.endPrice);
    if (startY !== null && endY !== null) {
      ctx.save();
      const color = data.anchor.direction === 'bullish' ? '#22c55e' : '#ef4444';
      ctx.strokeStyle = `${color}cc`;
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);
      ctx.beginPath();
      ctx.moveTo(anchorStartX, startY);
      ctx.lineTo(anchorEndX, endY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = color;
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText('Anchor', anchorStartX + 6, (startY + endY) / 2);
      ctx.restore();
    }
  }

  // Golden Zone — shaded band between 0.618 and 0.786.
  if (data.goldenZone) {
    const top = getYFromPrice(data.goldenZone.highPrice);
    const bot = getYFromPrice(data.goldenZone.lowPrice);
    if (top !== null && bot !== null) {
      const y = Math.min(top, bot);
      const h = Math.abs(bot - top);
      ctx.save();
      ctx.fillStyle = '#eab30826'; // ~15% gold
      ctx.fillRect(anchorEndX, y, zoneEndX - anchorEndX, h);
      ctx.strokeStyle = '#eab308b3';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(anchorEndX, y, zoneEndX - anchorEndX, h);
      ctx.setLineDash([]);
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.fillStyle = '#eab308';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('GOLDEN ZONE', anchorEndX + 6, y + 4);
      ctx.restore();
    }
  }

  // Retracement levels — horizontal dashed lines with ratio + price labels.
  data.levels?.forEach((lvl) => {
    const y = getYFromPrice(lvl.price);
    if (y === null || y < 0 || y > height) return;
    const isKey = KEY_RATIOS.has(Number(lvl.ratio.toFixed(3)));
    ctx.save();
    const color = isKey ? '#eab308' : '#94a3b8';
    ctx.strokeStyle = `${color}b3`;
    ctx.lineWidth = isKey ? 1.5 : 1;
    ctx.setLineDash(isKey ? [] : [4, 4]);
    ctx.beginPath();
    ctx.moveTo(anchorEndX, y);
    ctx.lineTo(zoneEndX, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = `${isKey ? 'bold ' : ''}10px Inter, sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(lvl.ratio.toFixed(3), zoneEndX - 4, y - 6);
    ctx.fillText(lvl.price.toFixed(5), zoneEndX - 4, y + 6);
    ctx.restore();
  });

  // Extensions — dotted projection lines.
  data.extensions?.forEach((lvl) => {
    const y = getYFromPrice(lvl.price);
    if (y === null || y < 0 || y > height) return;
    ctx.save();
    ctx.strokeStyle = '#22c55ecc';
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 6]);
    ctx.beginPath();
    ctx.moveTo(anchorEndX, y);
    ctx.lineTo(zoneEndX, y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = 'bold 10px Inter, sans-serif';
    ctx.fillStyle = '#22c55e';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    ctx.fillText(`ext ${lvl.ratio.toFixed(3)}`, zoneEndX - 4, y);
    ctx.restore();
  });
}
